import { FC, useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ImplicitFlow } from "@vacasa/react-components-lib";
import {
  IDP_CLIENT_ID,
  IDP_REDIRECT_URI,
  IDP_SCOPES,
  IDP_AUDIENCE,
  ENVIRONMENT,
} from "./core/constants";
import store from "./store";
import { Provider, useDispatch } from "react-redux";

import "./App.scss";

import PageNotFound from "./components/PageNotFound/PageNotFound";
import DataResolver from "./components/DataResolver/DataResolver";
import HomeDeviceGrid from "./components/HomeDeviceGrid/HomeDeviceGrid";
import DeviceMonitoring from "./components/DeviceMonitoring/DeviceMonitoring";
import DeviceMonitoringAlerts from "./components/DeviceMonitoringAlerts/DeviceMonitoringAlerts";
import PostAuthorization from "./components/AuthorizationRedirect/PostAuthorization";
import PreAuthorization from "./components/AuthorizationRedirect/PreAuthorization";
import EmbeddedAuth from "./components/AuthorizationRedirect/EmbeddedAuth";
import HomePage from "./components/HomePage";
import ReportGrid from "./components/Reporting/Reporting";
import { AppContextStore, AppOrigin } from "./store/app-context";

// Detect the origin of the application based on the parent window URL or referrer
function detectAppOrigin(): AppOrigin {
  const isInIframe = window.parent !== window;
  let originUrl = "";

  // Try to get origin from parent window (if in iframe)
  if (isInIframe) {
    try {
      originUrl = window.parent.location.origin;
    } catch (e) {
      // Cross-origin error - can't access parent.location
      // Fall back to referrer
      originUrl = document.referrer || "";
    }
  } else {
    // Not in iframe, use referrer
    originUrl = document.referrer || "";
  }

  // Normalize the URL to check
  const urlToCheck = originUrl.toLowerCase();

  // Check for GNT origin
  if (urlToCheck.includes("goodneighbortech.com")) {
    return "gnt";
  }

  // Check for Admin origin (Vacasa domains)
  if (
    urlToCheck.includes("admin.vacasa.com") ||
    urlToCheck.includes("admin.vacasastage.com")
  ) {
    return "admin";
  }

  // Default to admin if we can't determine
  return "admin";
}

// Detect origin once at module load and reuse everywhere
const detectedOrigin: AppOrigin = detectAppOrigin();

// AppContent component that uses Redux hooks - must be inside Provider
const AppContent: FC = () => {
  const dispatch = useDispatch();
  const origin = AppContextStore.selectors.useAppOrigin();

  // Initialize app origin on mount
  useEffect(() => {
    dispatch(AppContextStore.actions.setOrigin(detectedOrigin));
  }, [dispatch]);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<PostAuthorization />} />
        <Route path="/:propertyId" element={<DataResolver />}>
          <Route path="" element={<HomePage />} />
          <Route path="devices" element={<HomeDeviceGrid />} />
          <Route path="device-monitoring" element={<DeviceMonitoring />} />
          <Route
            path="device-monitoring/alerts"
            element={<DeviceMonitoringAlerts />}
          />
          {origin === "admin" ? (
            <Route path="reporting" element={<ReportGrid />} />
          ) : null}
        </Route>
        <Route path="*" element={<PageNotFound />} />
      </Routes>
    </BrowserRouter>
  );
};

function App() {
  // Detect origin to decide which auth flow to use
  const origin = detectedOrigin;

  // Use OneLogin/Authentik if origin is admin, otherwise use EmbeddedAuth
  if (origin === "admin") {
    // Check for directory hint in URL params
    const hint = new URLSearchParams(window.location.search).get(
      "directory_hint",
    );
    const directoryHint =
      hint && ["authentik", "onelogin"].includes(hint) ? hint : null;
    return (
      <Provider store={store}>
        <PreAuthorization>
          <ImplicitFlow
            directoryHint={directoryHint || "onelogin"}
            clientId={IDP_CLIENT_ID}
            redirectUrl={IDP_REDIRECT_URI}
            scopes={IDP_SCOPES}
            audience={IDP_AUDIENCE}
            tokenRefresh={true}
            // on local authenticate on stage
            env={ENVIRONMENT === "local" ? "stage" : (ENVIRONMENT as any)}
          >
            <AppContent />
          </ImplicitFlow>
        </PreAuthorization>
      </Provider>
    );
  }

  // Use EmbeddedAuth for GNT or unknown origins
  return (
    <Provider store={store}>
      <EmbeddedAuth>
        <AppContent />
      </EmbeddedAuth>
    </Provider>
  );
}

export default App;
